import { useState, useRef, useEffect } from "react";
import { t, sampleNames } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";
import { getName } from "../profile.js";
import { saveTrophy } from "../prizes.js";
import PrizePicker from "../PrizePicker.jsx";
import Model3D from "../Model3D.jsx";

/* ==================== RADIOBILER – sumo-dysten ====================
   Skub de andre radiobiler UD over kanten af den runde arena.
   To runder: INDLEDENDE (6 biler, de sidste 3 overlever og går videre)
   og FINALEN (3 biler, hårdere bots: hurtigere + jagter spilleren og
   passer bedre på kanten). Spillerens bil er BAMSE-RADIOBILEN (maskot-
   hoved bag rattet). Vinder man finalen: GULDPOKAL (saveTrophy).
   Nr. 2/3 i finalen: mellem bamse (PrizePicker åbner direkte).
   Styring som Racerbiler: joystick nederst th., tryk i banehalvdele,
   piletaster/A-D. Bilen gasser selv op — input drejer kun.
=================================================================== */

const W = 360, H = 540;
const ARENA = { x: 180, y: 236, r: 152 };
const CAR_R = 15;
const PLAYER_MAX = 150;
const STEER = 4.2;                 // rad/s ved fuldt udslag (strammet 17/7)
const JOY = { x: 292, y: 478, r: 52 };   // forstørret 17/7 (Daniels ønske — pladsen er der)
const R1_BOTS = 5;                 // indledende: spiller + 5
const SURVIVE = 3;                 // videre til finalen
const BOT = {
  r1:    { max: 118, steer: 2.3, edgeCare: 46, aggro: 0.45 },
  final: { max: 152, steer: 3.1, edgeCare: 60, aggro: 0.75 },
};

function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    count(hi) { const c = ensure(); if (c) tone(hi ? 880 : 440, c.currentTime, 0.16, "square", 0.1); },
    bump(p) {
      const c = ensure(); if (!c) return;
      tone(120 + p * 80, c.currentTime, 0.12, "square", 0.1 + p * 0.05);
      tone(70, c.currentTime + 0.01, 0.16, "sine", 0.09);
    },
    slam() {
      const c = ensure(); if (!c) return;
      tone(70, c.currentTime, 0.26, "square", 0.18);
      tone(48, c.currentTime + 0.02, 0.3, "sine", 0.15);
      [1150, 860].forEach((f, i) => tone(f, c.currentTime + 0.03 + i * 0.04, 0.1, "triangle", 0.07));
    },
    fall() {
      const c = ensure(); if (!c) return;
      const o = c.createOscillator(), g = c.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(500, c.currentTime);
      o.frequency.exponentialRampToValueAtTime(90, c.currentTime + 0.45);
      g.gain.setValueAtTime(0.1, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.5);
      o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.52);
    },
    fanfare() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046, 1318].forEach((f, i) => tone(f, c.currentTime + i * 0.11, 0.28, "triangle", 0.13));
    },
    lose() {
      const c = ensure(); if (!c) return;
      [300, 240, 180].forEach((f, i) => tone(f, c.currentTime + i * 0.16, 0.24, "sawtooth", 0.1));
    },
  };
}

const BOT_COLORS = ["#2f7fd4", "#3fae46", "#e08a2e", "#8e44ad", "#d966a8"];
const shortName = (n) => (n && n.length > 9 ? n.slice(0, 8) + "…" : n || "");

export default function Bump({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | result
  const [result, setResult] = useState(null);    // {outcome: "win"|"second"|"out"}
  const [picking, setPicking] = useState(false);
  const [hud, setHud] = useState({ round: 1, left: 0 });
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setResult(null);
    setPicking(false);
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const g = {
      t: 0,
      round: 1,                 // 1 = indledende, 2 = finalen
      cars: [],
      falling: [],              // {x,y,rot,scale,t,color,teddy}
      sparks: [],
      flashes: [],              // stjerne-flash ved sidetræf
      shake: 0,                 // skærmrystelse (decay)
      countdown: 3.4,
      banner: null,             // {txt, t, color}
      steer: 0, keys: {}, joy: null,
      over: false,
      botNames: sampleNames("race", R1_BOTS + 2),
    };
    if (localStorage.getItem("carnival.debug")) window.__bp = g;

    const mkCar = (name, color, isPlayer, skill) => ({
      name, color, isPlayer, skill,
      x: 0, y: 0, heading: 0, speed: 0,
      steer: 0, target: null, reroll: 0,
    });

    const placeCars = (list) => {
      list.forEach((c, i) => {
        const a = (i / list.length) * Math.PI * 2 - Math.PI / 2;
        c.x = ARENA.x + Math.cos(a) * ARENA.r * 0.55;
        c.y = ARENA.y + Math.sin(a) * ARENA.r * 0.55;
        c.heading = a + Math.PI;        // peger mod midten
        c.speed = 0;
      });
    };

    const setupRound = (round) => {
      g.round = round;
      g.pause = false;
      g.countdown = 3.4;
      g.falling = [];
      if (round === 1) {
        g.cars = [
          mkCar(null, "#c8352e", true, null),
          ...Array.from({ length: R1_BOTS }, (_, i) =>
            mkCar(g.botNames[i], BOT_COLORS[i % BOT_COLORS.length], false, BOT.r1)),
        ];
      } else {
        g.cars = [
          mkCar(null, "#c8352e", true, null),
          mkCar(g.botNames[R1_BOTS], "#2f7fd4", false, BOT.final),
          mkCar(g.botNames[R1_BOTS + 1], "#8e44ad", false, BOT.final),
        ];
      }
      placeCars(g.cars);
      setHud({ round, left: g.cars.length });
    };
    setupRound(1);

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- statisk baggrund (Gemini-mockup 17/7): STÅLPANEL m. nitter/ridser,
       pære-liste i toppen, metal-fane til rundeinfo, dobbelt neonring ---- */
    const bg = document.createElement("canvas");
    bg.width = W * 2; bg.height = H * 2;
    {
      const b = bg.getContext("2d");
      b.scale(2, 2);
      const rnd = (i) => { const v = Math.sin(i * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v); };
      // børstet stålpanel over det hele
      const steel = b.createLinearGradient(0, 0, 0, H);
      steel.addColorStop(0, "#3a4150"); steel.addColorStop(0.5, "#2b303c"); steel.addColorStop(1, "#20242e");
      b.fillStyle = steel; b.fillRect(0, 0, W, H);
      for (let i = 0; i < 70; i++) {
        b.strokeStyle = `rgba(255,255,255,${0.015 + rnd(i) * 0.02})`;
        b.lineWidth = 1;
        const y = rnd(i * 3) * H;
        b.beginPath(); b.moveTo(0, y); b.lineTo(W, y + (rnd(i * 7) - 0.5) * 6); b.stroke();
      }
      // ridser + panel-nitter langs kanten
      for (let i = 0; i < 16; i++) {
        const a = rnd(i * 11) * Math.PI * 2;
        const x = rnd(i * 5) * W, y = rnd(i * 9) * H;
        b.strokeStyle = `rgba(255,255,255,${0.04 + rnd(i * 13) * 0.05})`;
        b.lineWidth = 1.2;
        b.beginPath(); b.moveTo(x, y);
        b.lineTo(x + Math.cos(a) * (10 + rnd(i) * 26), y + Math.sin(a) * (10 + rnd(i) * 26));
        b.stroke();
      }
      const rivet = (x, y, r = 3) => {
        const gr = b.createRadialGradient(x - 1, y - 1, 0.5, x, y, r);
        gr.addColorStop(0, "#7a8494"); gr.addColorStop(1, "#1c2028");
        b.fillStyle = gr;
        b.beginPath(); b.arc(x, y, r, 0, Math.PI * 2); b.fill();
      };
      for (let x = 12; x < W; x += 48) { rivet(x, H - 10); }
      for (let y = 78; y < H - 14; y += 52) { rivet(10, y); rivet(W - 10, y); }
      // pære-liste i toppen (fatninger m. skiftevis tændte pærer)
      const strip = b.createLinearGradient(0, 6, 0, 26);
      strip.addColorStop(0, "#1e222c"); strip.addColorStop(1, "#2e3440");
      b.fillStyle = strip; b.fillRect(0, 4, W, 24);
      b.fillStyle = "rgba(255,255,255,0.08)"; b.fillRect(0, 4, W, 1.4);
      for (let i = 0; i < 12; i++) {
        const x = 18 + i * (W - 36) / 11;
        b.strokeStyle = "#4a5260"; b.lineWidth = 2;
        b.beginPath(); b.arc(x, 16, 5.4, 0, Math.PI * 2); b.stroke();
        if (i % 2) {
          b.fillStyle = "#ffd75e";
          b.shadowColor = "#ffce5e"; b.shadowBlur = 9;
          b.beginPath(); b.arc(x, 16, 3.6, 0, Math.PI * 2); b.fill();
          b.shadowBlur = 0;
          b.fillStyle = "rgba(255,255,255,0.7)";
          b.beginPath(); b.arc(x - 1.2, 14.8, 1.1, 0, Math.PI * 2); b.fill();
        } else {
          b.fillStyle = "#20242c";
          b.beginPath(); b.arc(x, 16, 3.6, 0, Math.PI * 2); b.fill();
        }
      }
      b.lineWidth = 1;
      // metal-FANE til rundeinfo (tekst tegnes dynamisk ovenpå)
      const tab = b.createLinearGradient(0, 30, 0, 68);
      tab.addColorStop(0, "#4a5260"); tab.addColorStop(1, "#333947");
      b.fillStyle = tab;
      b.beginPath();
      b.moveTo(0, 30); b.lineTo(168, 30); b.lineTo(190, 68); b.lineTo(0, 68);
      b.closePath(); b.fill();
      b.strokeStyle = "rgba(255,255,255,0.16)"; b.lineWidth = 1.4;
      b.beginPath(); b.moveTo(0, 30.7); b.lineTo(168, 30.7); b.lineTo(189, 67.3); b.stroke();
      b.lineWidth = 1;
      // faldet: mørk skygge-ring under platformen
      b.fillStyle = "rgba(0,0,0,0.55)";
      b.beginPath(); b.ellipse(ARENA.x, ARENA.y + 12, ARENA.r + 18, ARENA.r + 10, 0, 0, Math.PI * 2); b.fill();
      // arena-gulv (mørkere stål m. koncentriske cirkler)
      const fl = b.createRadialGradient(ARENA.x - 40, ARENA.y - 60, 30, ARENA.x, ARENA.y, ARENA.r + 4);
      fl.addColorStop(0, "#4a5260"); fl.addColorStop(0.65, "#363d49"); fl.addColorStop(1, "#242a34");
      b.fillStyle = fl;
      b.beginPath(); b.arc(ARENA.x, ARENA.y, ARENA.r, 0, Math.PI * 2); b.fill();
      for (const [rr2, al] of [[0.28, 0.14], [0.45, 0.1], [0.62, 0.08], [0.8, 0.06]]) {
        b.strokeStyle = `rgba(10,14,20,${al})`; b.lineWidth = 7;
        b.beginPath(); b.arc(ARENA.x, ARENA.y, ARENA.r * rr2, 0, Math.PI * 2); b.stroke();
        b.strokeStyle = "rgba(255,255,255,0.045)"; b.lineWidth = 1.6;
        b.beginPath(); b.arc(ARENA.x, ARENA.y, ARENA.r * rr2 + 4, 0, Math.PI * 2); b.stroke();
      }
      b.lineWidth = 1;
      // nittehuller + skrammer på gulvet
      for (let i = 0; i < 26; i++) {
        const a = (i / 26) * Math.PI * 2;
        b.fillStyle = "rgba(16,20,28,0.85)";
        b.beginPath(); b.arc(ARENA.x + Math.cos(a) * (ARENA.r - 10), ARENA.y + Math.sin(a) * (ARENA.r - 10), 2, 0, Math.PI * 2); b.fill();
      }
      for (let i = 0; i < 20; i++) {
        const a = rnd(i) * Math.PI * 2, rr3 = rnd(i * 3) * (ARENA.r - 24);
        b.strokeStyle = `rgba(255,255,255,${0.03 + rnd(i * 7) * 0.04})`;
        b.lineWidth = 1.3;
        b.beginPath();
        b.moveTo(ARENA.x + Math.cos(a) * rr3, ARENA.y + Math.sin(a) * rr3);
        b.lineTo(ARENA.x + Math.cos(a) * rr3 + (rnd(i * 13) - 0.5) * 28, ARENA.y + Math.sin(a) * rr3 + (rnd(i * 17) - 0.5) * 28);
        b.stroke();
      }
      b.lineWidth = 1;
      // DOBBELT neonring (mockuppet): kraftig ydre + tynd lys indre + halo
      b.strokeStyle = "rgba(79,216,232,0.18)"; b.lineWidth = 14;
      b.beginPath(); b.arc(ARENA.x, ARENA.y, ARENA.r + 5, 0, Math.PI * 2); b.stroke();
      b.strokeStyle = "#4fd8e8"; b.lineWidth = 4.5;
      b.shadowColor = "#4fd8e8"; b.shadowBlur = 16;
      b.beginPath(); b.arc(ARENA.x, ARENA.y, ARENA.r + 3, 0, Math.PI * 2); b.stroke();
      b.shadowBlur = 8;
      b.strokeStyle = "#b8f4fa"; b.lineWidth = 1.8;
      b.beginPath(); b.arc(ARENA.x, ARENA.y, ARENA.r - 3, 0, Math.PI * 2); b.stroke();
      b.shadowBlur = 0; b.lineWidth = 1;
      // vignette
      const vg = b.createRadialGradient(W / 2, H * 0.42, 140, W / 2, H * 0.46, 430);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(8,10,16,0.5)");
      b.fillStyle = vg; b.fillRect(0, 0, W, H);
    }

    /* ---- input ---- */
    const key = (e, down) => {
      if (["ArrowLeft", "a", "A"].includes(e.key)) { g.keys.left = down; e.preventDefault(); }
      if (["ArrowRight", "d", "D"].includes(e.key)) { g.keys.right = down; e.preventDefault(); }
    };
    const kd = (e) => key(e, true);
    const ku = (e) => key(e, false);
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    const toXY = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: ((p.clientX - r.left) / r.width) * W, y: ((p.clientY - r.top) / r.height) * H };
    };
    const pDown = (e) => {
      audioRef.current.unlock();
      e.preventDefault();
      const p = toXY(e);
      if (Math.hypot(p.x - JOY.x, p.y - JOY.y) < JOY.r + 22) g.joy = p;
      else g.steer = p.x < W / 2 ? -1 : 1;
    };
    const pMove = (e) => {
      if (!g.joy) return;
      g.joy = toXY(e);
    };
    const pUp = () => { g.joy = null; g.steer = 0; };
    canvas.addEventListener("mousedown", pDown);
    canvas.addEventListener("mousemove", pMove);
    window.addEventListener("mouseup", pUp);
    canvas.addEventListener("touchstart", pDown, { passive: false });
    canvas.addEventListener("touchmove", pMove, { passive: true });
    window.addEventListener("touchend", pUp);

    /* forbedret styring (Daniels ønske): joystikket er RETNINGS-styring —
       peg derhen du vil køre, bilen drejer mod den vinkel. Taster/side-tryk
       er stadig ren venstre/højre. */
    const playerSteer = (c) => {
      if (g.joy) {
        const dx = g.joy.x - JOY.x, dy = g.joy.y - JOY.y;
        const mag = Math.hypot(dx, dy);
        if (mag > JOY.r * 0.22) {
          const want = Math.atan2(dy, dx);
          let d = want - c.heading;
          while (d > Math.PI) d -= Math.PI * 2;
          while (d < -Math.PI) d += Math.PI * 2;
          return Math.max(-1, Math.min(1, d * 2.6));
        }
        return 0;
      }
      if (g.keys.left) return -1;
      if (g.keys.right) return 1;
      return g.steer;
    };

    /* ---- AI: sigt BAG modstanderen (skub dem udad), pas på kanten ---- */
    const botSteer = (c, dt) => {
      const sk = c.skill;
      c.reroll -= dt;
      if (c.reroll <= 0 || !c.target || !g.cars.includes(c.target)) {
        c.reroll = 0.8 + Math.random() * 1.2;
        const foes = g.cars.filter((o) => o !== c);
        const player = foes.find((o) => o.isPlayer);
        c.target = (player && Math.random() < sk.aggro) ? player
          : foes[Math.floor(Math.random() * foes.length)];
      }
      const tgt = c.target;
      let aimX, aimY;
      const distEdge = ARENA.r - Math.hypot(c.x - ARENA.x, c.y - ARENA.y);
      if (distEdge < sk.edgeCare) {
        aimX = ARENA.x; aimY = ARENA.y;          // væk fra kanten!
      } else if (tgt) {
        const ox = tgt.x - ARENA.x, oy = tgt.y - ARENA.y;
        const m = Math.hypot(ox, oy) || 1;
        aimX = tgt.x + (ox / m) * 26;            // punkt BAG målet (mod kanten)
        aimY = tgt.y + (oy / m) * 26;
      } else { aimX = ARENA.x; aimY = ARENA.y; }
      const want = Math.atan2(aimY - c.y, aimX - c.x);
      let d = want - c.heading;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      return Math.max(-1, Math.min(1, d * 2.4));
    };

    const eliminate = (c) => {
      g.cars = g.cars.filter((x) => x !== c);
      g.falling.push({ x: c.x, y: c.y, rot: c.heading, scale: 1, t: 0, color: c.color, teddy: c.isPlayer });
      audioRef.current.fall();
      g.banner = {
        txt: c.isPlayer ? t("bp_you_out") : t("bp_out", { name: shortName(c.name) }),
        t: 0, color: c.isPlayer ? "#ff8a7a" : "#F6EED9", small: !c.isPlayer,
      };
      setHud({ round: g.round, left: g.cars.length });
    };

    const finish = (outcome) => {
      if (g.over) return;
      g.over = true;
      if (outcome === "win") {
        saveTrophy({ game: t("tile_bump") });
        audioRef.current.fanfare();
      } else if (outcome === "second") audioRef.current.fanfare();
      else audioRef.current.lose();
      setTimeout(() => {
        setResult({ outcome });
        if (outcome === "second") setPicking(true);   // mellem bamse -> direkte valg
        setPhase("result");
      }, 900);
    };

    /* ---- step ---- */
    const ageFx = (dt) => {
      g.sparks.forEach((s) => { s.t += dt; s.x += s.vx * dt; s.y += s.vy * dt; });
      g.sparks = g.sparks.filter((s) => s.t < 0.35);
      g.flashes.forEach((f) => (f.t += dt));
      g.flashes = g.flashes.filter((f) => f.t < 0.32);
      if (g.shake > 0) g.shake = Math.max(0, g.shake - dt * 1.8);
      for (const c of g.cars) if (c.wobble) c.wobble = Math.max(0, c.wobble - dt * 1.6);
      g.falling.forEach((f) => {
        f.t += dt;
        f.scale = Math.max(0, 1 - f.t * 1.5);
        f.rot += dt * 6;
        f.y += dt * 60;
      });
      g.falling = g.falling.filter((f) => f.t < 0.8);
    };

    const step = (dt) => {
      g.t += dt;
      if (g.banner) { g.banner.t += dt; if (g.banner.t > 1.6) g.banner = null; }

      if (g.countdown > 0) {
        const before = Math.ceil(g.countdown);
        g.countdown -= dt;
        const after = Math.ceil(Math.max(g.countdown, 0));
        if (after !== before && after > 0) audioRef.current.count(false);
        if (g.countdown <= 0) audioRef.current.count(true);
        return;
      }
      if (g.over || g.pause) {         // kvalifikations-pausen fryser fysikken
        ageFx(dt);                     // men gnister/flash/rystelse skal dø ud
        return;
      }

      // styring + fremdrift
      for (const c of g.cars) {
        const input = c.isPlayer ? playerSteer(c) : botSteer(c, dt);
        const steerRate = c.isPlayer ? STEER : c.skill.steer;
        c.heading += input * steerRate * dt;
        const max = c.isPlayer ? PLAYER_MAX : c.skill.max;
        c.speed += (max - c.speed) * 1.6 * dt;
        c.x += Math.cos(c.heading) * c.speed * dt;
        c.y += Math.sin(c.heading) * c.speed * dt;
      }

      // kollisioner: elastisk + ekstra skub (sammenstød er hele spillet)
      for (let i = 0; i < g.cars.length; i++) {
        for (let j = i + 1; j < g.cars.length; j++) {
          const A = g.cars[i], B2 = g.cars[j];
          const dx = B2.x - A.x, dy = B2.y - A.y;
          const d = Math.hypot(dx, dy);
          if (d > 0.01 && d < CAR_R * 2) {
            const nx = dx / d, ny = dy / d;
            const push = (CAR_R * 2 - d) / 2 + 0.5;
            A.x -= nx * push; A.y -= ny * push;
            B2.x += nx * push; B2.y += ny * push;
            // fart-komposanter langs normalen byttes + skub-faktor
            const va = Math.cos(A.heading) * A.speed * nx + Math.sin(A.heading) * A.speed * ny;
            const vb = Math.cos(B2.heading) * B2.speed * nx + Math.sin(B2.heading) * B2.speed * ny;
            const rel = va - vb;
            if (rel > 0) {
              /* SIDETRÆF (Daniels ønske): normalen vinkelret på OFRETS
                 køreretning = T-bone → markant hårdere skub + effekter */
              const side = Math.abs(Math.sin(B2.heading - Math.atan2(ny, nx)));
              const slam = rel > 85 && side > 0.6;
              const imp = (rel * 0.9 + 40) * (1 + side * 0.9);
              B2.x += nx * imp * dt * 6; B2.y += ny * imp * dt * 6;
              B2.wobble = Math.min((B2.wobble || 0) + side * (rel / 220), 0.9);
              A.speed *= 0.82;
              const hx = (A.x + B2.x) / 2, hy = (A.y + B2.y) / 2;
              if (slam) {
                audioRef.current.slam();
                g.shake = Math.min(0.35 + rel / 600, 0.6);
                g.flashes.push({ x: hx, y: hy, t: 0 });
              } else {
                audioRef.current.bump(Math.min(rel / 200, 1));
              }
              for (let s = 0; s < (slam ? 14 : 5); s++) {
                g.sparks.push({
                  x: hx, y: hy,
                  vx: (Math.random() - 0.5) * (slam ? 260 : 160),
                  vy: (Math.random() - 0.5) * (slam ? 260 : 160),
                  t: 0,
                });
              }
            }
          }
        }
      }

      // ud over kanten?
      for (const c of [...g.cars]) {
        if (Math.hypot(c.x - ARENA.x, c.y - ARENA.y) > ARENA.r - CAR_R * 0.25) {
          eliminate(c);
        }
      }

      ageFx(dt);

      // runde-logik
      const player = g.cars.find((c) => c.isPlayer);
      if (!player) { finish(g.round === 2 ? "second" : "out"); return; }
      if (g.round === 1 && g.cars.length <= SURVIVE) {
        // spilleren er blandt de sidste 3: videre!
        g.pause = true;
        g.banner = { txt: t("bp_qualified"), t: 0, color: "#7ae08a" };
        setTimeout(() => { if (!g.over) setupRound(2); }, 1700);
      } else if (g.round === 2 && g.cars.length === 1) {
        finish("win");
      }
    };

    /* ---- tegning ---- */
    const rrp = (c, x, y, w2, h2, r) => {
      c.beginPath();
      c.moveTo(x + r, y);
      c.arcTo(x + w2, y, x + w2, y + h2, r);
      c.arcTo(x + w2, y + h2, x, y + h2, r);
      c.arcTo(x, y + h2, x, y, r);
      c.arcTo(x, y, x + w2, y, r);
      c.closePath();
    };

    /* radiobilen: AFRUNDET KVADRAT (Daniels ønske) m. kromkofanger hele vejen
       rundt — fysikken er stadig en cirkel (CAR_R), formen er kun visuel */
    const drawCar = (x, y, heading, color, isPlayer, scale = 1, alpha = 1) => {
      const L = CAR_R * 2.15, B = CAR_R * 1.9;   // længde x bredde
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(x, y);
      // farvet glød under bilen (mockuppet: guld under spilleren)
      const glowC = isPlayer ? "255,215,94" : (() => {
        const n = parseInt(color.slice(1), 16);
        return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
      })();
      const gl = ctx.createRadialGradient(0, 0, 4, 0, 0, CAR_R + 10);
      gl.addColorStop(0, `rgba(${glowC},${0.34 * alpha})`);
      gl.addColorStop(1, `rgba(${glowC},0)`);
      ctx.fillStyle = gl;
      ctx.beginPath(); ctx.arc(0, 0, CAR_R + 10, 0, Math.PI * 2); ctx.fill();
      ctx.rotate(heading);
      ctx.scale(scale, scale);
      // skygge
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      rrp(ctx, -L / 2 + 1, -B / 2 + 4, L, B, 10); ctx.fill();
      // krom-kofanger (den ydre afrundede firkant)
      const chrome = ctx.createLinearGradient(-L / 2, -B / 2, L / 2, B / 2);
      chrome.addColorStop(0, "#e8ecf0"); chrome.addColorStop(0.5, "#9aa4ae"); chrome.addColorStop(1, "#5f6a74");
      ctx.fillStyle = chrome;
      rrp(ctx, -L / 2, -B / 2, L, B, 10); ctx.fill();
      ctx.strokeStyle = "rgba(30,36,44,0.6)"; ctx.lineWidth = 1.4;
      rrp(ctx, -L / 2, -B / 2, L, B, 10); ctx.stroke();
      // karrosseri
      ctx.fillStyle = color;
      rrp(ctx, -L / 2 + 3.4, -B / 2 + 3.4, L - 6.8, B - 6.8, 7); ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.26)";
      ctx.beginPath(); ctx.ellipse(-4, -5.5, 7, 3.6, -0.4, 0, Math.PI * 2); ctx.fill();
      // forrude (bue foran føreren)
      ctx.strokeStyle = "rgba(220,240,250,0.65)"; ctx.lineWidth = 2.4;
      ctx.beginPath(); ctx.arc(4.5, 0, 7.5, -1.1, 1.1); ctx.stroke();
      ctx.lineWidth = 1;
      // næse-lygte (retning)
      ctx.fillStyle = "rgba(255,235,160,0.95)";
      ctx.beginPath(); ctx.arc(L / 2 - 3.6, 0, 2.4, 0, Math.PI * 2); ctx.fill();
      if (isPlayer) {
        // maskotten bag rattet: hoved + ører + creme-snude
        ctx.fillStyle = "#a9713d";
        ctx.beginPath(); ctx.arc(-4.6, -5.4, 2.6, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(4.6, -5.4, 2.6, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(0, -1, 7, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#f0e0c0";
        ctx.beginPath(); ctx.ellipse(0, 1.4, 3.6, 2.8, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#2b1d0a";
        ctx.beginPath(); ctx.arc(-2.4, -2.4, 1, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(2.4, -2.4, 1, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(0, 0.6, 1, 0, Math.PI * 2); ctx.fill();
      } else {
        // bot: hjelm
        ctx.fillStyle = "#f2f5f8";
        ctx.beginPath(); ctx.arc(0, -1, 5.4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "rgba(0,0,0,0.35)";
        ctx.beginPath(); ctx.arc(1.6, -1, 2.6, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      ctx.globalAlpha = 1;
    };

    const draw = () => {
      // skærmrystelse ved sidetræf
      ctx.save();
      if (g.shake > 0) {
        ctx.translate((Math.random() - 0.5) * 9 * g.shake, (Math.random() - 0.5) * 9 * g.shake);
      }
      ctx.drawImage(bg, 0, 0, W, H);
      // faldende biler (under de aktive)
      for (const f of g.falling) drawCar(f.x, f.y, f.rot, f.color, f.teddy, f.scale, Math.max(0, 1 - f.t));
      // gnister
      for (const s of g.sparks) {
        ctx.globalAlpha = Math.max(0, 1 - s.t / 0.35);
        ctx.fillStyle = "#ffd75e";
        ctx.shadowColor = "#ffd75e"; ctx.shadowBlur = 6;
        ctx.beginPath(); ctx.arc(s.x, s.y, 2, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0; ctx.globalAlpha = 1;
      }
      // stjerne-flash ved T-bone
      for (const f of g.flashes) {
        const k = Math.max(0, 1 - f.t / 0.32);
        ctx.globalAlpha = k;
        ctx.fillStyle = "#fff3c8";
        ctx.beginPath();
        for (let i = 0; i < 10; i++) {
          const a = (i / 10) * Math.PI * 2 + f.t * 4;
          const r = i % 2 ? 7 : 20 + (1 - k) * 10;
          ctx[i ? "lineTo" : "moveTo"](f.x + Math.cos(a) * r, f.y + Math.sin(a) * r);
        }
        ctx.closePath(); ctx.fill();
        ctx.strokeStyle = "#ffd75e"; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(f.x, f.y, 14 + (1 - k) * 22, 0, Math.PI * 2); ctx.stroke();
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
      // biler + navne (eget navn i GULD m. stjerne som i Racerbiler)
      for (const c of g.cars) {
        const wob = c.wobble ? Math.sin(g.t * 34) * c.wobble * 0.35 : 0;
        drawCar(c.x, c.y, c.heading + wob, c.color, c.isPlayer);
        ctx.font = "800 9.5px system-ui, sans-serif";
        ctx.textAlign = "center";
        if (c.isPlayer) {
          ctx.font = "800 10.5px system-ui, sans-serif";
          ctx.lineWidth = 3; ctx.strokeStyle = "rgba(6,8,18,0.8)";
          const label = "★ " + shortName(getName() || "");
          ctx.strokeText(label, c.x, c.y - CAR_R - 7);
          ctx.fillStyle = "#ffd75e";
          ctx.fillText(label, c.x, c.y - CAR_R - 7);
          ctx.lineWidth = 1;
        } else {
          ctx.fillStyle = "rgba(246,238,217,0.75)";
          ctx.fillText(shortName(c.name), c.x, c.y - CAR_R - 5);
        }
      }
      // joystick: knurlet krom-knap (mockuppets BUMPER-knap-look)
      ctx.beginPath(); ctx.arc(JOY.x, JOY.y, JOY.r + 3, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(14,18,26,0.8)"; ctx.fill();
      const ring = ctx.createLinearGradient(JOY.x - JOY.r, JOY.y - JOY.r, JOY.x + JOY.r, JOY.y + JOY.r);
      ring.addColorStop(0, "#8a94a4"); ring.addColorStop(0.5, "#4a515e"); ring.addColorStop(1, "#767f8e");
      ctx.strokeStyle = ring; ctx.lineWidth = 5;
      ctx.beginPath(); ctx.arc(JOY.x, JOY.y, JOY.r, 0, Math.PI * 2); ctx.stroke();
      // knurling: korte radiale hak hele vejen rundt
      ctx.strokeStyle = "rgba(16,20,28,0.7)"; ctx.lineWidth = 1.4;
      for (let i = 0; i < 36; i++) {
        const a = (i / 36) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(JOY.x + Math.cos(a) * (JOY.r - 2.4), JOY.y + Math.sin(a) * (JOY.r - 2.4));
        ctx.lineTo(JOY.x + Math.cos(a) * (JOY.r + 2.4), JOY.y + Math.sin(a) * (JOY.r + 2.4));
        ctx.stroke();
      }
      const face = ctx.createRadialGradient(JOY.x - 8, JOY.y - 10, 4, JOY.x, JOY.y, JOY.r - 4);
      face.addColorStop(0, "#3c434f"); face.addColorStop(1, "#232833");
      ctx.fillStyle = face;
      ctx.beginPath(); ctx.arc(JOY.x, JOY.y, JOY.r - 5, 0, Math.PI * 2); ctx.fill();
      ctx.lineWidth = 2;
      // 2D-knop: viser retningen der peges (retnings-styringen)
      let kx = 0, ky = 0;
      if (g.joy) {
        const dx = g.joy.x - JOY.x, dy = g.joy.y - JOY.y;
        const mag = Math.hypot(dx, dy) || 1;
        const k = Math.min(mag, JOY.r * 0.55);
        kx = (dx / mag) * k; ky = (dy / mag) * k;
      } else if (g.keys.left || g.steer === -1) kx = -22;
      else if (g.keys.right || g.steer === 1) kx = 22;
      const knob = ctx.createRadialGradient(JOY.x + kx - 4, JOY.y + ky - 5, 3, JOY.x + kx, JOY.y + ky, 19);
      knob.addColorStop(0, "#e8ecf0"); knob.addColorStop(1, "#6a7480");
      ctx.fillStyle = knob;
      ctx.beginPath(); ctx.arc(JOY.x + kx, JOY.y + ky, 17, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "rgba(20,24,32,0.5)";
      ctx.beginPath(); ctx.arc(JOY.x + kx, JOY.y + ky, 17, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 1;
      // runde-info på metal-fanen
      ctx.font = "800 13px 'Alfa Slab One', Georgia, serif";
      ctx.textAlign = "left";
      ctx.shadowColor = "#4fd8e8"; ctx.shadowBlur = 6;
      ctx.fillStyle = "#4fd8e8";
      ctx.fillText(g.round === 2 ? t("bp_final") : t("bp_round1"), 12, 47, 160);
      ctx.shadowBlur = 0;
      ctx.font = "800 11px system-ui, sans-serif";
      ctx.fillStyle = "rgba(246,238,217,0.85)";
      ctx.fillText(t("bp_left", { n: g.cars.length }), 12, 62, 160);
      // nedtælling: metallisk tal m. mørk kontur (mockuppet)
      if (g.countdown > 0) {
        const n = Math.ceil(g.countdown - 0.4);
        const txt = n > 0 ? String(n) : t("bp_go");
        ctx.font = "bold 64px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 9; ctx.strokeStyle = "rgba(10,14,22,0.85)";
        ctx.strokeText(txt, ARENA.x, ARENA.y + 20);
        if (n > 0) {
          const mg = ctx.createLinearGradient(0, ARENA.y - 40, 0, ARENA.y + 22);
          mg.addColorStop(0, "#e8ecf2"); mg.addColorStop(0.5, "#9aa4b2"); mg.addColorStop(1, "#5f6a78");
          ctx.fillStyle = mg;
        } else ctx.fillStyle = "#7ae08a";
        ctx.fillText(txt, ARENA.x, ARENA.y + 20);
        ctx.lineWidth = 2; ctx.strokeStyle = "rgba(255,255,255,0.35)";
        ctx.strokeText(txt, ARENA.x, ARENA.y + 20);
        ctx.lineWidth = 1;
      }
      // banner
      if (g.banner) {
        const k = g.banner.t;
        ctx.globalAlpha = Math.min(1, (1.6 - k) * 2.4);
        ctx.font = g.banner.small ? "bold 17px 'Alfa Slab One', Georgia, serif" : "bold 26px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(6,8,18,0.85)";
        ctx.strokeText(g.banner.txt, W / 2, 110 - k * 12, W - 40);
        ctx.fillStyle = g.banner.color;
        ctx.fillText(g.banner.txt, W / 2, 110 - k * 12, W - 40);
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
      ctx.restore();   // skærmrystelsen
    };

    let raf, last = performance.now();
    const loop = (ts) => {
      const dt = Math.min((ts - last) / 1000, 0.033);
      last = ts;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
      canvas.removeEventListener("mousedown", pDown);
      canvas.removeEventListener("mousemove", pMove);
      window.removeEventListener("mouseup", pUp);
      canvas.removeEventListener("touchstart", pDown);
      canvas.removeEventListener("touchmove", pMove);
      window.removeEventListener("touchend", pUp);
    };
  }, [phase]);

  /* ================= UI ================= */
  return (
    <div className={"bp-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <h1>{t("tile_bump").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("bp_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("bp_lede")}</p>
          <ul className="rules">
            <li>{t("bp_rule1")}</li>
            <li>{t("bp_rule2")}</li>
            <li>{t("bp_rule3")}</li>
          </ul>
          <button className="cta" onClick={startGame}>{t("bp_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{hud.round === 2 ? t("bp_final") : t("bp_round1")}</span>
            <span>{t("bp_left", { n: hud.left })}</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "result" && result && !picking && (
        <div className="overlay">
          <div className="card">
            <div className={"ribbon" + (result.outcome === "out" ? " dark" : "")}>
              {result.outcome === "win" ? t("bp_res_win")
                : result.outcome === "second" ? t("bp_res_second") : t("bp_res_out")}
            </div>
            {result.outcome === "win" && (
              <>
                <p className="trophybig"><Model3D src="/models/trophy.glb?v=2" thumb="/images/thumbs/trophy.png" fallback="🏆" size={130} /></p>
                <p className="bp-sub">{t("bp_res_win_sub")}</p>
              </>
            )}
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {picking && (
        <PrizePicker
          size="mellem"
          game={t("tile_bump")}
          onDone={() => setPicking(false)}
        />
      )}
    </div>
  );
}

const css = `
.bp-root{
  --cream:#F6EED9; --ink:#22301c; --red:#C8352E;
  min-height:100vh; background:#0a0614;
  background-image:radial-gradient(circle at 50% -10%, #241640 0%, #0a0614 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(22px,6.4vw,38px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #2a1a50, 0 6px 12px rgba(0,0,0,.5);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.cta{
  width:100%; margin-top:14px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.alt{margin-top:10px; background:#3d3a6a; box-shadow:0 4px 0 #232048; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #232048;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #2a2450; box-shadow:0 12px 30px rgba(0,0,0,.6);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(5,4,14,.8); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:20px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#3d3a6a;}
.trophybig{margin:4px 0 6px; display:flex; justify-content:center;}
.bp-sub{font-weight:800; font-size:14px; margin:0 0 8px;}
`;
